import type { Signal} from '@angular/core';
import { Injectable, computed, signal } from '@angular/core';

import type { AppConfig } from './app-config.model';

/**
 * Carga y expone la configuración de runtime (`/config.json`).
 *
 * NO usa HttpClient a propósito: el fetch ocurre en el APP_INITIALIZER, antes de
 * que el árbol de interceptores (auth/tenant/trace) esté disponible; usar
 * HttpClient acá crearía una dependencia circular con AuthStore.
 */
@Injectable({ providedIn: 'root' })
export class AppConfigService {
  private readonly _config = signal<AppConfig | null>(null);

  /** Config cargada. Lanza si se lee antes del initializer (fail-fast). */
  readonly config: Signal<AppConfig> = computed(() => {
    const cfg = this._config();
    if (!cfg) {
      throw new Error(
        '[AppConfigService] config.json no cargado todavía. ' +
          '¿Falta provideAppConfig() en el APP_INITIALIZER?',
      );
    }
    return cfg;
  });

  /** True una vez que la config está disponible. */
  readonly isLoaded = computed(() => this._config() !== null);

  /** Lee un feature flag de runtime con default seguro. */
  feature(name: string): boolean {
    return this._config()?.features?.[name] ?? false;
  }

  private loadPromise: Promise<void> | null = null;

  /**
   * Descarga `/config.json`. Idempotente: los APP_INITIALIZER async corren en
   * PARALELO (Angular no los encadena), así que cualquier initializer que
   * dependa de la config debe await load() — todos comparten el mismo fetch.
   * Si falla (HTTP o schema zod), propaga el error para abortar el bootstrap (fail-fast).
   */
  load(url = '/config.json'): Promise<void> {
    this.loadPromise ??= (async () => {
      // El schema (zod, ~70 kB raw) se carga como chunk LAZY en paralelo al fetch:
      // mantiene el bundle inicial bajo budget y, como el APP_INITIALIZER bloquea el
      // bootstrap de todos modos, no agrega latencia secuencial al arranque.
      const [{ parseAppConfig }, res] = await Promise.all([
        import('./app-config.schema'),
        fetch(url, { cache: 'no-store' }),
      ]);
      if (!res.ok) {
        throw new Error(`[AppConfigService] no se pudo cargar ${url}: HTTP ${res.status}`);
      }
      const raw: unknown = await res.json();
      // Valida contra el schema zod ANTES de exponer la config: un config.json
      // malformado aborta acá nombrando el campo exacto, no en runtime lejos de la causa.
      this._config.set(parseAppConfig(raw, url));
    })();
    return this.loadPromise;
  }
}
